import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Theme from "../../styles/Theme/Theme";
import { Footer } from "../App/Footer";
import { Sidebar } from "../App/SideBar";
import { ConfigurationHeader } from "../Global/Molecules/Configurations/ConfigurationHeader";
import { ConfigurationsSubTabs } from "../Global/Organism/Configurations/ConfigurationsSubTabs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100%;
  background-color: ${Theme.color["neutral-1"]};
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: calc(100% - 235px);
  overflow-y: auto;
  .tab_component_children {
    padding: ${Theme.spacing.s05};
    box-sizing: border-box;
  }
`;


/**
 * Finds the first allowed configuration subtab based on the given tags.
 *
 * @param {string[]} tags - The tags to filter the configurations subtabs.
 * @param {Object[]} configurationsSubTabs - The array of configurations subtabs to search from.
 * @returns {Object|undefined} - The first allowed configuration subtab, or undefined if none found.
 */
const findFirstAllowed = (tags, configurationsSubTabs) => {
    const res = configurationsSubTabs.filter(
        (value) => tags.includes(value.label.toLowerCase())
    );
    return res.length > 0 ? res[0] : undefined;
}

export const Configurations = () => {
    const tags = JSON.parse(localStorage.getItem("unified_nova_tags"));
    const { mainTab, subTab } = useParams();
    const navigate = useNavigate();

    const defaultTab = findFirstAllowed(tags, ConfigurationsSubTabs)?.path || "";

    const initialTab =
        mainTab && ConfigurationsSubTabs.some((tab) => tab.path === mainTab)
            ? mainTab
            : defaultTab;

    const tabItem = ConfigurationsSubTabs.find(
        (entry) => entry.path === initialTab
    );

    let initialHeaderSubTab = null;
    if (tabItem?.buttons && subTab && tabItem.subTabsPath.includes(subTab)) {
        initialHeaderSubTab = subTab;
    } else if (tabItem?.buttons) {
        initialHeaderSubTab = tabItem.subTabsPath[0];
    }

    const [navState, setNavState] = useState({
        currentTab: initialTab,
        headerSelectedTab: initialHeaderSubTab,
    });

    /**
     * When the internal state does not match the URL parameters, update the route.
     */
    useEffect(() => {
        if (
            navState.currentTab !== mainTab ||
            navState.headerSelectedTab !== subTab
        ) {
            navigate(
                `/configurations/${navState.currentTab}/${navState.headerSelectedTab}`,
                { replace: true }
            );
        }
    }, [navState, mainTab, subTab, navigate]);

    /**
     * Handles the change of the tab.
     * When changing the tab, the headerSelectedTab is set to the first subtab of the new tab.
     * @param {string} tab - The new tab to be selected.
     */
    const handleTabChange = (tab) => {
        const newTabItem = ConfigurationsSubTabs.find((entry) => entry.path === tab);
        if (newTabItem) {
            setNavState((prev) => ({
                ...prev,
                currentTab: tab,
                headerSelectedTab: newTabItem.subTabsPath[0],
            }));
        }
    };

    /**
     * Retrieves the component based on the current tab and header selected tab.
     * @returns {React.Component} The component associated with the current tab and header selected tab.
     */
    const getComponentByTab = () => {
        let item =
            ConfigurationsSubTabs.find((entry) => entry.path === navState.currentTab) ||
            findFirstAllowed(tags, ConfigurationsSubTabs);
        if (!item) return;
        if (!item.buttons) return item.component;
        const headerTabIndex = item.subTabsPath.findIndex(
            (subTabPath) => subTabPath === navState.headerSelectedTab
        );
        const finalIndex = headerTabIndex >= 0 ? headerTabIndex : 0;
        return item.component[finalIndex];
    };

    const OpenTabComponent = getComponentByTab();
    if (!OpenTabComponent) return null;

    const currentTabItem = ConfigurationsSubTabs.find(
        (entry) => entry.path === navState.currentTab
    );

    return (
        <Wrapper>
            <Sidebar
                sideBarMenu={ConfigurationsSubTabs.filter((e) =>
                    tags.includes(e?.label)
                )}
                activeTab={navState.currentTab}
                setCurrentTab={handleTabChange}
            />
            {currentTabItem && tags.includes(currentTabItem.label) && (
                <RightContent>
                    <div>
                        {tabItem?.buttons && tabItem.buttons.length > 1 && (
                            <ConfigurationHeader
                                buttons={tabItem.buttons}
                                changeHeaderSelectedTab={(sub) => {
                                    setNavState((prev) => ({
                                        ...prev,
                                        headerSelectedTab: sub
                                    }))
                                }}
                                headerSelectedTab={navState.headerSelectedTab}
                                subTabPath={tabItem.subTabsPath}
                            />
                        )}
                        <OpenTabComponent
                            headerSelectedTab={navState.headerSelectedTab}
                            className="tab_component_children"
                        />
                    </div>
                    <Footer />
                </RightContent>
            )}
        </Wrapper>
    );
};